import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Select from '@/finsight/ui/common/components/Select';
import ExportButtonWithSpinner from '@/Framework/UI/Atoms/Button/ExportButtonWithLoader';
import TopIssuersContextProvider from '@/finsight/application/TopCompanies/topCompaniesContexts/TopIssuersContext';
import { useParentPageContext } from '@/finsight/application/ParentPageContext';
import TopCompanyCommonContextProvider,
{ useTopCompanyCommonContext } from '@/finsight/application/TopCompanies/topCompaniesCommon/TopCompaniesCommonContext';
import TopRatingAgencyContextProvider
  from '@/finsight/application/TopCompanies/topCompaniesContexts/TopRatingAgenciesContext';
import TopUnderwritersContextProvider
  from '@/finsight/application/TopCompanies/topCompaniesContexts/TopUnderwritersContext';
import TopCompaniesTabs from './TopCompaniesTabs';
import {
  ISSUER_NAME,
  RATING_AGENCY_NAME,
  UNDERWRITER_NAME,
  PARENT_NAME,
  restrictedExportTopCompaniesDescription,
} from './constants';
import {
  TopIssuers,
  TopUnderwriters,
  TopRatingAgencies,
} from './TopCompaniesTables';
import CheckUserAccess from 'src/finsight/ui/common/components/CheckUserAccess';
import { UserPlanType } from '@/users/domain/UserPlan';
import TopCompaniesNoAccessFrame
  from '@/finsight/ui/common/components/TopCompanies/TopCompaniesTables/common/TopCompaniesNoAccessFrame';
import { RestrictedAccessExplanation } from '@/finsight/ui/common/components/Explanations';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';

import styles from './styles.scss';

const TOP_COMPANIES_CONFIG = {
  [ISSUER_NAME]: {
    title: 'Top Issuers',
    component: <TopIssuers />,
  },
  [PARENT_NAME]: {
    title: 'Issuers',
    component: <TopIssuers />,
  },
  [RATING_AGENCY_NAME]: {
    title: 'Top Rating Agencies',
    component: <TopRatingAgencies />,
  },
  [UNDERWRITER_NAME]: {
    title: 'Top Underwriters',
    component: <TopUnderwriters />,
  },
};

const TopCompanies = () => {
  const { query: {
    companyViewId,
  } } = useRouter();
  const {
    isExportAvailable,
    exportToExcel,
    isExportFetching,
    periodsCollection,
    selectedPeriod,
    setSelectedPeriod,
    activeTab,
  } = useTopCompanyCommonContext();
  const currentActiveTab = companyViewId && activeTab === ISSUER_NAME ? PARENT_NAME : activeTab;
  const [isExplanationOpen, setIsExplanationOpen] = useState(false);
  const { checkAccessByUserPlan } = useUserPlanContext();

  const toggleExplanation = () => setIsExplanationOpen((prev) => !prev);

  const ExportButton = (props) => (
    <ExportButtonWithSpinner
      className={ styles.exportWrapper }
      tooltipContainerClassName={ styles.exportButtonTooltip }
      iconClassName={ styles.exportButtonIcon }
      isFetching={ isExportFetching }
      onClick={ exportToExcel }
      dataTest={ companyViewId ? 'exportTopCompaniesParent' : 'exportTopCompaniesProfile' }
      { ...props }
    />
  );

  const PeriodSelect = (props) => (
    <Select
      name="topCompaniesTTMFilter"
      selectedOption={ selectedPeriod }
      options={ periodsCollection }
      onChangeSelectedOption={ setSelectedPeriod }
      tooltipContent="Change Period"
      dataTest={ companyViewId ? 'topCompaniesTTMFilterSelectParent' : 'topCompaniesTTMFilterSelectProfile' }
      { ...props }
    />
  );

  return (
    <div className={ styles.topParentsContainer } data-test="topCompaniesWidget">
      <div className={ styles.topParentsHeader }>
        <h3>{ TOP_COMPANIES_CONFIG[currentActiveTab].title }</h3>
        <div className={ styles.actionsContainer }>
          <CheckUserAccess
            checks={ [checkAccessByUserPlan(UserPlanType.STARTER)] }
            fallback={ (
              <>
                <ExportButton isDisabled />
                <PeriodSelect disabled />
              </>
            ) }
          >
            <>
              <CheckUserAccess
                checks={ [checkAccessByUserPlan(UserPlanType.PREMIUM)] }
                fallback={ (
                  <RestrictedAccessExplanation
                    title="Export the New Issue Summary"
                    description={ restrictedExportTopCompaniesDescription }
                    isOpen={ isExplanationOpen }
                    closeTooltip={ () => setIsExplanationOpen(false) }
                  >
                    <ExportButton isDisabled={ false } onClick={ toggleExplanation } />
                  </RestrictedAccessExplanation>
                ) }
              >
                <ExportButton isDisabled={ !isExportAvailable } />
              </CheckUserAccess>
              <PeriodSelect />
            </>
          </CheckUserAccess>
        </div>
      </div>
      <TopCompaniesTabs />

      <CheckUserAccess
        checks={ [checkAccessByUserPlan(UserPlanType.STARTER)] }
        fallback={ (
          <>
            <TopCompaniesNoAccessFrame />
            <div className={ styles.tableBlurred }>
              <div className={ styles.overlay } />
              { TOP_COMPANIES_CONFIG[currentActiveTab].component }
            </div>
          </>
        ) }
      >
        { TOP_COMPANIES_CONFIG[currentActiveTab].component }
      </CheckUserAccess>
    </div>
  );
};

export default () => {
  const { topParents } = useParentPageContext();

  return (
    <TopCompanyCommonContextProvider topParents={ topParents }>
      <TopIssuersContextProvider topParents={ topParents }>
        <TopRatingAgencyContextProvider>
          <TopUnderwritersContextProvider>
            <TopCompanies />
          </TopUnderwritersContextProvider>
        </TopRatingAgencyContextProvider>
      </TopIssuersContextProvider>
    </TopCompanyCommonContextProvider>
  );
};
